import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import PageHelmet from "../component/common/Helmet";
import HeaderAbs from "../component/header/HeaderAbs";
import FooterTwo from "../component/footer/FooterTwo";

const EnterpriseAI = () => {
    const marketplaceDeployments = useMemo(
        () => [
	            {
	                key: "legalmind",
	                title: "LegalMind — Contract Intelligence Accelerator",
	                oneLiner:
	                    "AWS-native contract copilot built on Bedrock Document Converse + Knowledge Bases: schema-first extraction with anchors, cited Q&A, and audit-ready traces.",
	                patternSummary:
	                    "Serverless: S3/CloudFront SPA + API Gateway/Lambda + Bedrock tools + DynamoDB/S3 persistence.",
	                hostingPattern:
	                    "Serverless SPA + API: S3 + CloudFront (OAC/WAF) frontend; API Gateway + Lambda orchestrator; SSE via Lambda Function URL behind CloudFront (origin-secret + strict CORS); AppConfig routes models/limits; outputs + citations persisted to DynamoDB/S3.",
	                marketplaceUrl: "https://aws.amazon.com/marketplace/pp/prodview-fcjw6afwpfg4c",
	                demoUrl: "https://d24fqcrgqg3n9g.cloudfront.net/legal/contracts/dashboard",
	                imageUrl: "/assets/images/proof/legalmind-card.svg",
	                proof: {
	                    diagramUrl: "/assets/images/proof/legalmind-architecture.png",
                    runbookUrl: "/assets/runbooks/legalmind-runbook.md",
                    traceUrl: "/assets/images/proof/legalmind-trace.svg",
                    templateType: "Packaged IaC: SAM / CloudFormation (serverless)",
                    securityBullets: [
                        "CloudFront + WAF at the edge; S3 origin protected (OAC) with deny-public bucket policy.",
                        "Encrypted storage (S3 SSE-KMS) and least-privilege IAM; sensitive config in Secrets Manager.",
                        "Credentialed SSE streaming locked down with origin-secret verification and strict CORS.",
                    ],
                    scopeBullets: [
                        "Extract contract fields/line items into governed JSON Schema with page-anchored highlights (Textract optional for OCR/anchors).",
                        "Ground Q&A with Bedrock Knowledge Bases and normalize citation objects for review.",
                        "Stream responses over SSE and persist trace/citation IDs for audit replay.",
                        "Operate via AppConfig-driven routing + CloudWatch EMF/X-Ray telemetry (latency, tokens, error categories).",
                    ],
                    architectureBullets: [
                        "React SPA uploads documents via presigned S3; orchestrator dispatches extraction/Q&A tools.",
                        "Chat and Q&A stream over SSE through CloudFront; answers include citations/anchors.",
                        "Sessions/jobs, trace IDs, and exports stored in DynamoDB/S3 for audit and downstream ingestion.",
                    ],
                    controlsBullets: [
                        "Edge hardening: CloudFront + WAF; S3 origin protected (OAC) and denied public access.",
                        "Streaming boundary: CloudFront routes `/api/chat/stream*` to Function URL origin with secret header + strict CORS.",
                        "Governance: schema validation + guardrails + least-privilege IAM; encrypted storage (KMS) and explicit retention.",
                    ],
                    decisions: [
                        {
                            decision: "Schema-first extraction (JSON Schema)",
                            options: "Freeform text vs typed JSON schemas",
                            why: "Enables validation and clean ingestion into CLM/BI.",
                            tradeoff: "Requires schema/prompt versioning and stricter validation.",
                        },
                        {
                            decision: "SSE streaming behind CloudFront",
                            options: "Chunked polling vs SSE response stream",
                            why: "Improves UX while keeping the origin protected behind CloudFront + origin secret.",
                            tradeoff: "Requires connection management and CORS correctness.",
                        },
                        {
                            decision: "Cited answers by default",
                            options: "Ungrounded vs citation-backed responses",
                            why: "Improves trust, review, and auditability.",
                            tradeoff: "Recall depends on knowledge coverage.",
                        },
                    ],
                    walkthrough: [
                        {
                            title: "1) Input (synthetic)",
                            code: `{\n  "document": "synthetic-msa.pdf",\n  "task": "Extract key fields"\n}`,
                        },
                        {
                            title: "2) Policy / validation",
                            code: `{\n  "checks": ["schema_valid", "citations_present"],\n  "result": "pass"\n}`,
                        },
                        {
                            title: "3) Output (sample JSON)",
                            code: `{\n  "governing_law": "Delaware",\n  "term_months": 24,\n  "risk_flags": ["auto_renewal"]\n}`,
                        },
                        {
                            title: "4) Audit record (sample)",
                            code: `{\n  "request_id": "req_...",\n  "citations": [{"page": 7}],\n  "timestamp": "2026-01-25T00:00:00Z"\n}`,
                        },
                    ],
                },
            },
	            {
	                key: "careflow",
	                title: "Careflow — Ambient Clinical Documentation",
	                oneLiner:
	                    "HIPAA-ready ambient documentation + follow-up automation with real-time Nova Sonic speech-to-speech streaming and tenant-scoped portals.",
	                patternSummary:
	                    "Realtime: Web Audio → API Gateway WebSocket → Lambda broker → Bedrock Realtime (Nova Sonic) via PrivateLink, plus Step Functions/EventBridge follow-ups.",
	                hostingPattern:
	                    "Realtime + workflow pattern: Web Audio/AudioWorklet → API Gateway WebSocket → Lambda broker → Bedrock Realtime (Nova Sonic) via PrivateLink; Cognito RBAC; AppConfig feature flags; Step Functions/EventBridge follow-ups; CloudFront + WAF + CloudWatch/X-Ray.",
	                marketplaceUrl: "https://aws.amazon.com/marketplace/pp/prodview-5udnvuvm235gi",
	                imageUrl: "/assets/images/proof/careflow-card.svg",
	                proof: {
	                    diagramUrl: "/assets/images/proof/careflow-architecture.png",
                    runbookUrl: "/assets/runbooks/careflow-security-posture.md",
                    traceUrl: "/assets/images/proof/careflow-trace.svg",
                    templateType: "Packaged IaC: CloudFormation/CDK (serverless + workflow)",
                    securityBullets: [
                        "PHI boundaries: Bedrock runtime access via PrivateLink (VPC endpoints) and strict network controls.",
                        "AuthZ: Cognito JWT + RBAC groups; edge protection via CloudFront + WAF managed rules.",
                        "Encryption + ops: KMS at rest, structured logs/metrics, and production-grade tracing/alarms.",
                    ],
                    scopeBullets: [
                        "Stream real-time encounters (audio/text/tool events) and generate structured notes and summaries.",
                        "Deliver a modular portal shell (Ambient/Doctor/Nurse) toggled per tenant via AppConfig.",
                        "Orchestrate follow-ups and campaigns via Step Functions/EventBridge (integrations as needed).",
                        "Ship operational runbooks + observability (EMF metrics, X-Ray traces, RUM dashboards, reconnect patterns).",
                    ],
                    architectureBullets: [
                        "Browser streams audio frames over WebSocket; Lambda maintains the Bedrock realtime session and translates events.",
                        "Notes/workflow state persisted in DynamoDB/S3; follow-ups scheduled through orchestration services.",
                        "Security posture designed for regulated data: PrivateLink to Bedrock runtime and edge protection via CloudFront + WAF.",
                    ],
                    controlsBullets: [
                        "Auth + RBAC: Cognito JWT and group-based access controls at connect and API boundaries.",
                        "PHI boundaries: PrivateLink/VPC endpoints to Bedrock; encryption at rest (KMS) and explicit retention controls.",
                        "Reliability + ops: canaries/alarms, tracing/metrics, and resilient WebSocket reconnect/backoff behavior.",
                    ],
                    decisions: [
                        {
                            decision: "Realtime WebSocket streaming",
                            options: "Batch transcription vs bidirectional stream",
                            why: "Supports low-latency interaction and immediate reliability signals.",
                            tradeoff: "More complex networking, retry logic, and session management.",
                        },
                        {
                            decision: "Feature-flagged portals via AppConfig",
                            options: "One-size-fits-all UI vs per-tenant enablement",
                            why: "Enables controlled rollout of portal experiences per tenant without rebuilds.",
                            tradeoff: "Requires configuration governance and release discipline.",
                        },
                        {
                            decision: "PrivateLink to Bedrock runtime",
                            options: "Public egress vs private endpoints",
                            why: "Reduces exposure for PHI traffic and aligns to HIPAA posture.",
                            tradeoff: "Adds VPC networking complexity.",
                        },
                    ],
                    walkthrough: [
                        {
                            title: "1) Input (synthetic)",
                            code: `{\n  "event": "ws_connected",\n  "session_id": "sess_..."\n}`,
                        },
                        {
                            title: "2) Streaming",
                            code: `{\n  "event": "audio_chunk",\n  "seq": 42,\n  "bytes": 8192\n}`,
                        },
                        {
                            title: "3) Output (sample note)",
                            code: `{\n  "summary": "...",\n  "assessment": "...",\n  "plan": ["..."]\n}`,
                        },
                        {
                            title: "4) Audit/trace (sample)",
                            code: `{\n  "trace_id": "trace_...",\n  "latency_ms": 820,\n  "errors": 0\n}`,
                        },
                    ],
                },
            },
	            {
	                key: "healthbridge",
	                title: "HealthBridge — Agent Navigator",
	                oneLiner:
	                    "Guardrail-first portal navigation agent powered by Nova Act: page-scoped actions, schema-validated Status JSON, and dry-run-by-default safety.",
	                patternSummary:
	                    "Container: ECS/Fargate runtime + Nova Act (Playwright) with dry-run-by-default, AppConfig gates, and per-step Status JSON persisted with DynamoDB TTL.",
	                hostingPattern:
	                    "Container pattern: ECS/Fargate tasks run Nova Act wrapper (Playwright); AppConfig gates dry-run vs mutate; per-page Status JSON persisted to DynamoDB with TTL; optional artifacts in S3 SSE-KMS + lifecycle; ECR ScanOnPush.",
	                marketplaceUrl: "https://aws.amazon.com/marketplace/pp/prodview-uf4aqot4w3fm2",
	                imageUrl: "/assets/images/proof/healthbridge-card.svg",
	                proof: {
	                    diagramUrl: "/assets/images/proof/healthbridge-architecture.png",
                    runbookUrl: "/assets/runbooks/healthbridge-operator-guide.md",
                    traceUrl: "/assets/images/proof/healthbridge-trace.svg",
                    templateType: "Packaged IaC: ECS/Fargate deployment templates (container-based)",
                    securityBullets: [
                        "Container posture: ECR images with ScanOnPush; ECS task IAM scoped to required resources.",
                        "Rollout safety: dry-run by default; mutations enabled only via explicit AppConfig gates.",
                        "Auditability: per-step Status JSON persisted with TTL; artifacts stored with SSE-KMS and lifecycle policies.",
                    ],
                    scopeBullets: [
                        "Implement portal drivers as page-scoped prompts + JSON Schema contracts (bounded max_steps, retries).",
                        "Default dry-run with mutation detection; enable state-changing actions only via AppConfig gates.",
                        "Persist evidence-grade Status JSON per page (trace/tenant/job/page/result/error) for audit and portal-change monitoring.",
                        "Deliver hardened container runtime (ECS/Fargate templates, ECR scanning, encryption, retention, least-privilege IAM).",
                    ],
                    architectureBullets: [
                        "Driver resolves portal/page and calls `act_page(prompt, schema)` with trace context and guardrails.",
                        "Each page emits Status JSON to DynamoDB (TTL); artifacts/screenshots captured only when enabled.",
                        "AppConfig controls rollout per portal; secrets in Secrets Manager; logs to CloudWatch with explicit retention.",
                    ],
                    controlsBullets: [
                        "Dry-run by default; mutations (submit/continue/upload) blocked unless explicitly enabled.",
                        "Auditability: structured Status JSON persisted with TTL and correlation IDs for review and replay.",
                        "Supply chain + data controls: ECR ScanOnPush, SSE-KMS storage, lifecycle policies, and least-privilege IAM.",
                    ],
                    decisions: [
                        {
                            decision: "Page-scoped contracts (schemas)",
                            options: "Brittle RPA selectors vs semantic page contracts",
                            why: "Improves resilience to portal changes and yields queryable outputs.",
                            tradeoff: "Requires schema/prompt iteration over time.",
                        },
                        {
                            decision: "Dry-run + mutation gates",
                            options: "Autonomous writes vs operator-controlled rollout",
                            why: "Reduces risk and supports regulated adoption.",
                            tradeoff: "Adds approval/enablement workflow for mutations.",
                        },
                    ],
                    walkthrough: [
                        {
                            title: "1) Intent (sample)",
                            code: `{\n  "goal": "Navigate portal and collect fields",\n  "mode": "dry_run"\n}`,
                        },
                        {
                            title: "2) Proposed action",
                            code: `{\n  "action": "click",\n  "target": "Apply button",\n  "requires_approval": true\n}`,
                        },
                        {
                            title: "3) Status JSON",
                            code: `{\n  "step": 3,\n  "state": "proposed",\n  "approved": false\n}`,
                        },
                        {
                            title: "4) Operator audit",
                            code: `{\n  "operator": "reviewer",\n  "decision": "deny",\n  "reason": "unexpected_field"\n}`,
                        },
                    ],
                },
            },
        ],
        []
    );

    return (
        <React.Fragment>
            <PageHelmet
                pageTitle="Enterprise AI"
                description="Enterprise AI implementations and AWS Marketplace accelerators — agent orchestration, governed RAG, and measurable delivery patterns."
                canonical="https://www.theomefford.com/enterprise-ai"
            />

            <HeaderAbs homeLink="/" logo="symbol-dark" color="color-black" />

            <div className="rn-page-title-area pt--120 pb--70 bg_color--1">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="blog-single-page-title text-center pt--60">
                                <h2 className="title theme-gradient">Enterprise AI</h2>
                                <p className="description">
                                    Enterprise AI platforms and forward-deployed accelerators — built for adoption, governance, and production reliability.
                                </p>
                                <div className="mt--30 d-flex justify-content-center flex-wrap" style={{ gap: "12px" }}>
                                    <Link className="rn-button-style--2 btn-solid" to="/recent-work">
                                        Recent Work
                                    </Link>
                                    <Link className="rn-button-style--2 btn-solid" to="/contact">
                                        Contact
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Palantir Foundry forward-deployed engineering */}
            <div id="foundry" className="rn-blog-area pt--90 pb--90 bg_color--5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2 className="title">Palantir Foundry — Forward Deployed Engineering</h2>
                                <h4 className="title">Principal Forward Deployed Engineer, Rackspace Technology</h4>
                            </div>
                        </div>
                    </div>

                    <div className="row mt--40">
                        <div className="col-lg-10 offset-lg-1">
                            <p className="description">
                                I’m a Principal Forward Deployed Engineer at Rackspace Technology,
                                and the job wears several hats. On the technical sales side I work
                                with the sales team to understand what a customer is actually trying
                                to fix, and to decide whether Foundry is the right platform for it —
                                sometimes the honest answer is that it isn’t, and saying so early is
                                worth more than a signed statement of work. When Foundry is the right
                                fit, I take the problem into initial architecture and solution
                                engineering, then bridge into proof of value and bootcamp: working
                                directly with the customer alongside our engineering team to get the
                                first real thing built. I stay involved through full delivery to make
                                sure the architecture that ships is still the one that solves the
                                business problem. As a full-stack FDE, my own work concentrates on AI
                                engineering with AIP and on agentic automation.
                            </p>
                            <p className="description">
                                Alongside customer work I lead our internal enablement. I founded a
                                Foundry Center of Excellence and built a set of reusable assets and a
                                Foundry harness — skills, tools, gates, and conventions packaged so
                                that, paired with the Palantir MCP, a junior engineer starts from our
                                best practices instead of rediscovering them. What we learn on
                                engagements gets written back into the harness, so the practice
                                compounds rather than living in a few people’s heads.
                            </p>
                            <p className="description">
                                The program I’m proudest of is an opportunity-to-quote rebuild for a
                                global solar manufacturer, where we brought time to quote from
                                roughly fourteen days to under three.
                            </p>

                            <div className="mt--30 d-flex flex-wrap" style={{ gap: "12px" }}>
                                <Link className="rn-button-style--2 btn-solid" to="/recent-work#foundry-fde">
                                    Read the full case study
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* AWS Marketplace deployments */}
            <div id="marketplace" className="rn-blog-area pt--90 pb--120 bg_color--1">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12">
                            <div className="section-title text-center">
                                <h2 className="title">AWS Marketplace Deployments</h2>
                                <h4 className="title">Production in ~6 weeks — architecture, security posture, and enablement included.</h4>
                                <p className="description" style={{ maxWidth: 900, margin: "0 auto" }}>
                                    Click a diagram to open full size.
                                </p>
                            </div>
                        </div>
                    </div>
		                    <div className="row mt--50">
		                        {marketplaceDeployments.map((deployment) => (
	                            <div key={deployment.key} className="col-lg-4 col-md-6 col-12 mt--30">
	                                <div className="blog blog-style--1 rn-work-card rn-marketplace-card">
	                                    <div className="content">
		                                        <h4 className="title">{deployment.title}</h4>
		                                        <div className="blog-text">
		                                            <p>{deployment.oneLiner}</p>
			                                            <div className="rn-deployed-evidence mt--20">
			                                                <h5 className="title">Deployed on AWS evidence</h5>
		                                                <p className="description">
		                                                    <strong>Hosting pattern:</strong> {deployment.hostingPattern}
		                                                </p>
		                                                <p className="description">
		                                                    <strong>Deployment option:</strong> {deployment.proof.templateType}
		                                                </p>
			                                                <a
			                                                    href={deployment.proof.diagramUrl}
			                                                    target="_blank"
			                                                    rel="noopener noreferrer"
		                                                    className="d-block mt--20"
		                                                    aria-label={`${deployment.title} architecture diagram (open full size)`}
		                                                >
		                                                    <img
		                                                        className="w-100"
		                                                        src={deployment.proof.diagramUrl}
		                                                        alt={`${deployment.title} architecture diagram`}
		                                                        loading="lazy"
		                                                        decoding="async"
		                                                        style={{
		                                                            border: "1px solid rgba(29, 29, 36, 0.12)",
		                                                            borderRadius: 10,
		                                                            background: "#ffffff",
		                                                            maxHeight: 240,
		                                                            objectFit: "contain",
		                                                        }}
		                                                    />
		                                                </a>
		                                                <div className="description">
		                                                    <strong>Security posture:</strong>
		                                                    <ul className="mt--10">
		                                                        {deployment.proof.securityBullets.map((bullet) => (
		                                                            <li key={bullet}>{bullet}</li>
		                                                        ))}
		                                                    </ul>
		                                                </div>
		                                            </div>
		                                        </div>

	                                        <div className="mt--30 d-flex flex-wrap" style={{ gap: "10px" }}>
	                                            <a
	                                                className="rn-button-style--2 btn-solid"
	                                                href={deployment.marketplaceUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                AWS Marketplace
                                            </a>
	                                            {deployment.demoUrl && (
	                                                <a
	                                                    className="rn-button-style--2 btn-solid"
	                                                    href={deployment.demoUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Live Demo
                                                </a>
	                                            )}
	                                            <Link className="rn-button-style--2 rn-btn-dark" to={`/runbooks/${deployment.key}`}>
	                                                Runbook
	                                            </Link>
	                                        </div>

	                                        <details className="rn-proof-panel mt--30">
	                                            <summary className="rn-proof-panel__summary">
	                                                Details: scope · architecture · controls · evidence
	                                            </summary>

                                            <div className="rn-proof-panel__body mt--20">
                                                <h5 className="title">What shipped (6-week scope)</h5>
                                                <ul>
                                                    {deployment.proof.scopeBullets.map((bullet) => (
                                                        <li key={bullet}>{bullet}</li>
                                                    ))}
                                                </ul>

                                                <h5 className="title mt--30">Architecture in 30 seconds</h5>
                                                <div className="case-study-diagram__frame mt--20">
                                                    <img
                                                        src={deployment.proof.diagramUrl}
                                                        alt={`${deployment.title} architecture diagram`}
                                                    />
                                                </div>
                                                <ul className="mt--20">
                                                    {deployment.proof.architectureBullets.map((bullet) => (
                                                        <li key={bullet}>{bullet}</li>
                                                    ))}
                                                </ul>

                                                <h5 className="title mt--30">Controls &amp; governance</h5>
                                                <ul>
                                                    {deployment.proof.controlsBullets.map((bullet) => (
                                                        <li key={bullet}>{bullet}</li>
                                                    ))}
                                                </ul>

                                                <h5 className="title mt--30">Demo walkthrough (synthetic)</h5>
                                                <div className="case-study-evidence mt--20">
                                                    {deployment.proof.walkthrough.map((step) => (
                                                        <div key={step.title} className="case-study-evidence__card">
                                                            <h4 className="title">{step.title}</h4>
                                                            <pre>
                                                                <code>{step.code}</code>
                                                            </pre>
                                                        </div>
                                                    ))}
                                                </div>

                                                <h5 className="title mt--30">Key decisions</h5>
                                                <div className="rn-decisions-table-wrapper mt--20">
                                                    <table className="rn-decisions-table">
                                                        <thead>
                                                            <tr>
                                                                <th>Decision</th>
                                                                <th>Options</th>
                                                                <th>Why</th>
                                                                <th>Tradeoff</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {deployment.proof.decisions.map((row) => (
                                                                <tr key={row.decision}>
                                                                    <td>{row.decision}</td>
                                                                    <td>{row.options}</td>
                                                                    <td>{row.why}</td>
                                                                    <td>{row.tradeoff}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>

                                                <h5 className="title mt--30">Evidence links</h5>
                                                <div className="mt--20 d-flex flex-wrap" style={{ gap: "10px" }}>
                                                    <a
                                                        className="rn-button-style--2 btn-solid"
                                                        href={deployment.marketplaceUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Marketplace
                                                    </a>
                                                    {deployment.demoUrl && (
                                                        <a
                                                            className="rn-button-style--2 btn-solid"
                                                            href={deployment.demoUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            Live Demo
                                                        </a>
                                                    )}
                                                    <a
                                                        className="rn-button-style--2 rn-btn-dark"
                                                        href={deployment.proof.diagramUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Diagram
                                                    </a>
                                                    <Link
                                                        className="rn-button-style--2 rn-btn-dark"
                                                        to={`/runbooks/${deployment.key}`}
                                                    >
                                                        Runbook
                                                    </Link>
                                                    <a
                                                        className="rn-button-style--2 rn-btn-dark"
                                                        href={deployment.proof.traceUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Trace
                                                    </a>
                                                </div>
                                            </div>
                                        </details>
	                                    </div>
	                                </div>
	                            </div>
	                        ))}
	                    </div>
                        <div className="row mt--50">
                            <div className="col-lg-12 text-center">
                                <Link className="rn-button-style--2 btn-solid" to="/contact">
                                    Request private artifacts
                                </Link>
                            </div>
                        </div>
	                </div>
	            </div>

            {/* FAIR internal products */}
            <div className="rn-blog-details pt--80 pb--120 bg_color--5">
                <div className="container">
                    <div className="row">
                        <div className="col-lg-10 offset-lg-1">
                            <div className="inner-wrapper">
                                <div className="inner mb--50">
                                    <h3 className="title">Enterprise AI implementations — built for adoption, not demos</h3>
                                    <p>
                                        I build enterprise AI systems that move from prototype to real use—grounded, governed, and measurable. My work spans internal copilots and
                                        agentic workflows under Rackspace’s Foundry for AI (FAIR), a practice focused on accelerating responsible AI adoption through “Ideate, Incubate,
                                        Industrialize.”{" "}
                                        <a
                                            href="https://www.rackspace.com/blog/fair-ai-adoption"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            (FAIR overview)
                                        </a>
                                    </p>
                                </div>

                                <div className="inner mb--50">
                                    <h4 className="title">ICE — enterprise co-worker pattern</h4>
                                    <p>
                                        <strong>What it is:</strong> an enterprise-ready generative AI “co-worker” designed to operate on curated enterprise data in a secure, private
                                        tenant.{" "}
                                        <a
                                            href="https://www.rackspace.com/newsroom/rackspace-technology-launches-intelligent-co-worker-ice-enterprise"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            (Press release)
                                        </a>
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Capabilities:</strong> HR copilots and enterprise knowledge experiences (search + synthesis) designed to reduce time-to-answer while
                                            maintaining governance and secure tenancy.
                                        </li>
                                        <li>
                                            <strong>Productionization:</strong> private-tenant data boundaries → retrieval grounding → evaluation/quality checks → audit signals → staged
                                            rollout and enablement.
                                        </li>
                                        <li>
                                            <strong>Outcomes:</strong> adoption-focused experiences for knowledge workers; measurable deflection/time-to-answer improvements vary by
                                            deployment.
                                        </li>
                                    </ul>
                                    <div className="mt--20">
                                        <a
                                            className="rn-button-style--2 btn-solid"
                                            href="https://fair.rackspace.com/insights/enhance-recruiting-generative-ai-hr/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Read: Generative AI in HR
                                        </a>
                                    </div>
                                </div>

                                <div className="inner mb--50">
                                    <h4 className="title">RITA — IT service copilot with citations</h4>
                                    <p>
                                        <strong>What it is:</strong> an interactive chatbot for IT service requests and self-service workflows (e.g., policy Q&A, password resets,
                                        hardware/software requests), designed to link responses back to source documentation.{" "}
                                        <a
                                            href="https://www.rackspace.com/newsroom/rackspace-technology-announces-fair-learn"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            (Public overview)
                                        </a>
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Capabilities:</strong> self-service IT assistance with grounded answers and source traceability (citations/anchors to policy content).
                                        </li>
                                        <li>
                                            <strong>Productionization:</strong> request intake → guided resolution → escalation paths; tuned for enterprise workflows and operational
                                            visibility.
                                        </li>
                                        <li>
                                            <strong>Outcomes:</strong> improved IT support deflection and faster time-to-resolution; outcomes vary by rollout and knowledge coverage.
                                        </li>
                                    </ul>
                                    <div className="mt--20">
                                        <a
                                            className="rn-button-style--2 btn-solid"
                                            href="https://www.rackspace.com/newsroom/rackspace-technology-announces-fair-learn"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Read: FAIR Learn announcement
                                        </a>
                                    </div>
                                </div>

                                <div className="inner mb--50">
                                    <h4 className="title">AskHR — agentic HR support at scale</h4>
                                    <p>
                                        <strong>What it is:</strong> an HR co-worker designed to handle repetitive employee questions with 24x7 support and multilingual coverage.{" "}
                                        <a
                                            href="https://www.linkedin.com/posts/rackspace-technology_agentic-ai-at-scale-the-rackspace-story-activity-7417615100871884800-FP55"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            (Public post)
                                        </a>
                                    </p>
                                    <ul>
                                        <li>
                                            <strong>Capabilities:</strong> HR knowledge + workflow automation that reduces ticket burden while maintaining grounded responses and clear
                                            escalation paths.
                                        </li>
                                        <li>
                                            <strong>Productionization:</strong> safe automation pattern: retrieval grounding, escalation when needed, and operational visibility (audit
                                            signals + quality monitoring).
                                        </li>
                                        <li>
                                            <strong>Outcomes (publicly stated):</strong> unresolved HR queries down 50%, HR ticket volume nearly cut in half, and 24x7x365 multilingual
                                            coverage.
                                        </li>
                                    </ul>
                                    <div className="mt--20">
                                        <a
                                            className="rn-button-style--2 btn-solid"
                                            href="https://www.linkedin.com/posts/rackspace-technology_agentic-ai-at-scale-the-rackspace-story-activity-7417615100871884800-FP55"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            View: AskHR outcomes
                                        </a>
                                    </div>
                                </div>

                                <div className="inner">
                                    <details>
                                        <summary className="title">FLO — placeholder (details on request)</summary>
                                        <div className="mt--20">
                                            <p>
                                                I couldn’t locate authoritative public detail tying “FLO” to FAIR’s internal products. If FLO is internal-only, this section is intentionally
                                                a template — I can share details in a private conversation.
                                            </p>
                                            <ul>
                                                <li>
                                                    <strong>Purpose:</strong> [e.g., finance/ops workflow automation, ticket triage, knowledge copilot]
                                                </li>
                                                <li>
                                                    <strong>Users:</strong> [who uses it, what workflows it supports]
                                                </li>
                                                <li>
                                                    <strong>Key capabilities:</strong> [RAG, agentic actions, approvals, integrations]
                                                </li>
                                                <li>
                                                    <strong>Governance:</strong> [audit logs, eval gates, RBAC, data boundaries]
                                                </li>
                                                <li>
                                                    <strong>Outcomes:</strong> [time saved, deflection, cycle-time reduction, quality improvements]
                                                </li>
                                            </ul>
                                            <div className="mt--20">
                                                <Link className="rn-button-style--2 btn-solid" to="/contact">
                                                    Request details
                                                </Link>
                                            </div>
                                        </div>
                                    </details>
                                </div>

                                <div className="inner mt--60">
                                    <p>
                                        <strong>How I deliver:</strong> secure data boundaries, grounded retrieval, evaluation gates, observability, and controlled rollout — built to move
                                        beyond testing to enterprise deployment.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FooterTwo />
        </React.Fragment>
    );
};

export default EnterpriseAI;
